import * as cookie from "cookie";
import type { SynchronousStorage } from "./types/SynchronousStorage.ts";

const getDefaultSetOptions = (): cookie.SerializeOptions => {
  // get domain - accounts.kivra.com -> .kivra.com (domain needs to have min 2 dots)
  // get domain - localhost -> undefined (don't set domain)
  const hostNameArr = window.location.hostname.split(".");
  const domain = hostNameArr.length >= 2
    ? `.${hostNameArr.slice(-2).join(".")}`
    : undefined;
  const path = "/";
  return {
    path,
    domain,
  };
};

export const useCookieStorage = <T>({
  key,
  parse = JSON.parse,
  serialize = JSON.stringify,
  options = {},
}: {
  key: string;
  parse?: (text: string) => T;
  serialize?: (data: T) => string;
  options?: cookie.SerializeOptions & cookie.ParseOptions;
}): SynchronousStorage<T> => {
  const { decode, ...setOptions } = options;
  return {
    get() {
      const cookies = cookie.parse(document.cookie, { decode });
      if (!cookies[key]) {
        return null;
      }
      try {
        return parse(cookies[key]);
      } catch {
        this.remove();
        return null;
      }
    },
    remove() {
      const defaultSetOptions = getDefaultSetOptions();
      document.cookie = cookie.serialize(key, "", {
        ...defaultSetOptions,
        ...setOptions,
        expires: new Date(0),
        maxAge: 0,
      });
      return null;
    },
    set(value) {
      const defaultSetOptions = getDefaultSetOptions();
      document.cookie = cookie.serialize(key, serialize(value), {
        ...defaultSetOptions,
        ...setOptions,
      });
    },
  };
};
